import dayjs from "dayjs";
import { useLottie } from "lottie-react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getChromeVersion } from "waapi/front-utils/helpers/getChromeVersion.js";
import { captureMessage } from "waapi/front-utils/sentry/captureMessage/index.js";
import { Flex } from "waapi/waap-ui/components/Flex/Flex.js";
import { FloatingLanguageSelect } from "../../app/components/FloatingLanguageSelect/index.js";
import { LOCK_SEND_SENTRY_MESSAGE_ON_LOADING_PAGE, LOTTIE_LOADING_WAALIEN, REPORT_TO_SENTRY_AFTER_30_SECONDS } from "./constants.js";
import { LoadingPageProps, LoadingTimeoutContext } from "./types.js";
import { LazyLuciolesBackground } from "../../app/components/LuciolesBackground/LuciolesBackground.lazy.js";
import { LoadingPageContainer } from "./styles.js";

const LoadingPageContext = createContext<{
    loading: number;
    extra: LoadingTimeoutContext;
    setLoading: (loading: boolean, timeoutContext?: LoadingTimeoutContext) => void;
} | null>(null);

export const LoadingPageProvider = ({ children }: { children: React.ReactNode }) => {
    const [loading, setLoading] = useState(0);
    const [extra, setExtra] = useState<LoadingTimeoutContext>({});

    const setLoadingWithExtra = (isLoading: boolean, timeoutContext?: LoadingTimeoutContext) => {
        if (isLoading) {
            setLoading((prev) => prev + 1);
        } else {
            setLoading((prev) => prev - 1);
        }
        setExtra(timeoutContext ?? {});
    };

    const value = useMemo(() => ({ loading, extra, setLoading: setLoadingWithExtra }), [loading, extra]);

    return <LoadingPageContext.Provider value={value}>{children}</LoadingPageContext.Provider>;
};

export const useLoadingPage = () => {
    const context = useContext(LoadingPageContext);

    if (!context) {
        throw new Error("useLoadingPage must be used within a LoadingPageProvider");
    }

    return context;
};

export const LoadingWaalienView = (props: { extra: LoadingTimeoutContext }) => {
    const { extra } = props;

    const { View } = useLottie(LOTTIE_LOADING_WAALIEN);

    const chromeVersion = getChromeVersion();

    if (chromeVersion && chromeVersion > 143) {
        return View;
    }

    useEffect(() => {
        const timeout = setTimeout(() => {
            const lockDate = localStorage.getItem(LOCK_SEND_SENTRY_MESSAGE_ON_LOADING_PAGE);

            if (lockDate && dayjs(lockDate).isAfter(dayjs())) {
                return;
            }

            const message = `User has seen loading page for too long`;
            captureMessage(message, {
                level: "error",
                description: `The user has been stuck on the loading page for ${REPORT_TO_SENTRY_AFTER_30_SECONDS / 1_000} seconds. We raise this error because this means the user had a bad experience with the app by seeing the loading page for too long.`,
                extra,
            });
            localStorage.setItem(LOCK_SEND_SENTRY_MESSAGE_ON_LOADING_PAGE, dayjs().add(1, "days").toISOString());
        }, REPORT_TO_SENTRY_AFTER_30_SECONDS);

        return () => {
            clearTimeout(timeout);
        };
    }, [JSON.stringify(extra)]);

    return View;
};

export const FlexLoading = (props: { extra: LoadingTimeoutContext }) => {
    const { extra } = props;

    return (
        <Flex style={{ flex: 1 }} align="center" justify="center">
            <LoadingWaalienView extra={extra} />
        </Flex>
    );
};

/**
 * This component should be renderd only once at the root of the app.
 * To trigger it's display either use the `FullScreenLoadingPage` component or the `setLoading` function from the `useLoadingPage` hook.
 *
 * If you just need the default waalaxy loader use the `FlexLoading` component.
 */
export const FullScreenLoadingComponent = (props: LoadingPageProps) => {
    const { backgroundStyle, languageSelect = false } = props;

    const { loading, extra } = useLoadingPage();

    if (!loading) {
        return null;
    }

    return (
        <LoadingPageContainer display={loading > 0} align="center" justify="center" backgroundStyle={backgroundStyle}>
            {languageSelect && <FloatingLanguageSelect size="large" />}
            {backgroundStyle === "blue" && <LazyLuciolesBackground />}
            <LoadingWaalienView extra={extra} />
        </LoadingPageContainer>
    );
};

/**
 * Trigger the display of the full screen loading page. The loading page will be shown while this component is mounted.
 */
export const FullScreenLoadingPage = (props: { extra: LoadingTimeoutContext }) => {
    const { extra } = props;

    const { setLoading } = useLoadingPage();

    useEffect(() => {
        setLoading(true, extra);

        return () => {
            setLoading(false);
        };
    }, [JSON.stringify(extra)]);

    return null;
};
